import React from "react";

/**
 * Displays the card network logo using Paycomet's official sprite.
 *
 * The sprite is rendered at its fixed size (background-size: 120px 882px),
 * matching paycomet_pa.html. The div acts as a clipping window that shows
 * only the logo area (~52×35 px, left column of the sprite).
 *
 * Sprite: https://api.paycomet.com/gateway/images.png
 */

const SPRITE_URL = "https://api.paycomet.com/gateway/images.png";

/**
 * background-position Y for the first sprite layer (brand logo).
 * Positions extracted directly from the paycomet_pa.html CSS.
 */
const BRAND_Y: Record<string, number> = {
    default:                   -122,
    visa:                      -163, // Tested
    visa_electron:             -176,
    mastercard:                -248, // Tested
    maestro:                   -291, // Tested
    discover:                  -333, // Tested
    american_express:          -375, // Tested
    diners_club_international: -418, // Tested
    jcb:                       -459, // Tested
    china_union_pay:           -502, // Tested
    unionpay:                  -502,
    elo:                       -544, // Tested
    cirrus:                    -586, // BIN 6304 – Paycomet JS classifies as maestro
    uatp:                      -621, // 1354 1234 5678 911
    carnet:                    -671, // BIN 5061 – Paycomet JS classifies as maestro
    eftpos:                    -713, // BIN 5898 – Paycomet JS classifies as maestro
    vpay:                      -755,
    visa_dankort:              -797, // 4571 0000 0000 0001
    nspk_mir:                  -839, // 2200 0000 0000 0004
};

/** Normalises the brand name to match the map keys */
export const normalizeBrand = (brand: string): string =>
    brand.toLowerCase().replace(/[\s/-]/g, "_");

interface CardBrandLogoProps {
    /** Brand name, e.g. "VISA", "mastercard", "amex" */
    brand?: string;
    className?: string;
}

/**
 * Visible clip size.
 * The sprite is 120px wide; the logo occupies ~52px (= input's padding-left).
 * Each brand strip is ~35px tall.
 */
const CLIP_W = 52;
const CLIP_H = 35;

const CardBrandLogo: React.FC<CardBrandLogoProps> = ({brand, className = ""}) => {
    const key = brand ? normalizeBrand(brand) : "default";
    const y   = BRAND_Y[key] ?? BRAND_Y["default"];

    return (
        <div
            title={brand}
            role="img"
            aria-label={brand ?? "card"}
            className={`flex-shrink-0 ${className}`}
            style={{
                width:               CLIP_W,
                height:              CLIP_H,
                backgroundImage:     `url("${SPRITE_URL}")`,
                backgroundRepeat:    "no-repeat",
                /* Same background-size as the original CSS: 120×882 px */
                backgroundSize:      "120px 882px",
                /* X fixed at 2px (matches original CSS), Y varies by brand */
                backgroundPosition:  `2px ${y}px`,
            }}
        />
    );
};

export default CardBrandLogo;
